import { useState } from 'react';
import { formatCompactNumber } from '../lib/format.ts';
import { ChartFrame, ChartTooltip } from './ChartFrame.tsx';
import type { ChartBaseProps } from './ChartFrame.tsx';

export interface HeatCell {
  /** 0 is Monday, 6 is Sunday. */
  day: number;
  /** 0 to 23. */
  hour: number;
  value: number;
}

export interface HeatmapProps extends ChartBaseProps {
  cells: HeatCell[];
  format?: (n: number) => string;
  /** What a value counts, for the tooltip: "sessions". */
  unit?: string;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const LABEL_W = 36;
/** Sequential steps: one hue (data slot 1) mixed into the surface, light to strong. */
const STEPS = [0, 18, 34, 52, 72, 92];

/** The colour for a value: the surface at zero, then data-1 at increasing strength. */
export function heatColour(value: number, max: number): string {
  if (value <= 0 || max <= 0) return 'var(--surface-hover)';
  const i = Math.min(STEPS.length - 1, Math.max(1, Math.ceil((value / max) * (STEPS.length - 1))));
  return `color-mix(in oklab, var(--data-1) ${STEPS[i]}%, var(--surface))`;
}

/**
 * When things are busy (data-viz.md): hour by day, one hue from light to strong, 2 px of
 * surface between cells, a scale caption under it. Hover a cell for its exact value.
 */
export function Heatmap({ cells, format = formatCompactNumber, unit = '', height = 7 * 22 + 20, ...base }: HeatmapProps) {
  const [hover, setHover] = useState<HeatCell | null>(null);
  const grid = new Map(cells.map((c) => [`${c.day}:${c.hour}`, c.value]));
  const max = Math.max(0, ...cells.map((c) => c.value));
  const table = {
    columns: ['Day', ...Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'))],
    rows: DAYS.map((d, di) => [d, ...Array.from({ length: 24 }, (_, h) => format(grid.get(`${di}:${h}`) ?? 0))]),
  };

  return (
    <ChartFrame {...base} height={height} isEmpty={max <= 0} table={table}>
      {(width) => {
        const cw = Math.max(4, (width - LABEL_W) / 24);
        const ch = (height - 20) / 7;
        return (
          <>
            <svg width={width} height={height}>
              {DAYS.map((d, di) => (
                <text key={d} x={LABEL_W - 6} y={di * ch + ch / 2 + 4} textAnchor="end" fill="var(--fg-subtle)" fontSize={12} fontFamily="var(--font-sans)">
                  {d}
                </text>
              ))}
              {[0, 6, 12, 18].map((h) => (
                <text key={h} x={LABEL_W + h * cw} y={height - 4} fill="var(--fg-subtle)" fontSize={12} fontFamily="var(--font-sans)" data-axis-tick="">
                  {`${String(h).padStart(2, '0')}:00`}
                </text>
              ))}
              {DAYS.map((_, di) =>
                Array.from({ length: 24 }, (_, h) => {
                  const v = grid.get(`${di}:${h}`) ?? 0;
                  return (
                    <rect
                      key={`${di}:${h}`}
                      x={LABEL_W + h * cw + 1}
                      y={di * ch + 1}
                      width={cw - 2}
                      height={ch - 2}
                      rx={2}
                      fill={heatColour(v, max)}
                      onMouseEnter={() => setHover({ day: di, hour: h, value: v })}
                      onMouseLeave={() => setHover(null)}
                    />
                  );
                }),
              )}
            </svg>
            {hover && (
              <ChartTooltip
                width={width}
                left={LABEL_W + hover.hour * cw + cw / 2}
                top={hover.day * ch + ch / 2}
                heading={`${DAYS[hover.day]} ${String(hover.hour).padStart(2, '0')}:00`}
                rows={[{ key: 'v', label: unit || 'Value', colour: heatColour(hover.value, max), value: format(hover.value) }]}
              />
            )}
          </>
        );
      }}
    </ChartFrame>
  );
}
